<template>
  <page-template :is-loading="isSearchLoading || isLoading" :data-exists="true">
    <template #pageHeadline>
      {{ $t('eventsLog.pageHeadline') }}
    </template>
    <template #pageHeadlineRight>
      <div class="rbz-field server-group">
        <rbz-dropdown
          :items="serverGroupItems"
          type="menu"
          classes="server-group-dropdown"
          :placeholder="$t('eventsLog.serverGroupDpPlacehoder')"
          :value="selectedServerGroup"
          :can-clear-single-select="true"
          @update:value="addServerGroupToFilter"
        >
        </rbz-dropdown>
      </div>
    </template>
    <template #secondLevelHeaderEntire>
      <rbz-filter
        ref="rbzFilter"
        :search-disabled="isSearchLoading || isLoading"
        :default-time="defaultTime"
        :query-history="queryHistory"
        :is-query-history-loading="isQueryHistoryLoading"
        :show-query-history="true"
        @blur-filter="onBlurFilterInput"
        @clear-filter="clearSelectedServerGroups"
        @search="loadData"
      >
        <template #additionalButtons>
          <rbz-form-button
            variant="tertiary"
            icon="IconLink"
            :label="$t('eventsLog.openDashboard')"
            link-route-name="Dashboard"
            class="dashboard-link-button"
            data-qa="dashboard-link-button"
            :link-query="dashboardQuery"
          />
        </template>
      </rbz-filter>
    </template>
    <template #pageContent="props">
      <div class="events-log-table-wrapper" v-if="!isSearchLoading && !isLoading">
        <rbz-table
          :columns="columnOptions"
          :table-data="data"
          :sticky-header="true"
          :class="{ 'rbz-table-panel-open': panelEventLogIndex !== null }"
          :row-classes="
            (item) => {
              if (item?.['request_id'] === panelEventLog?.['request_id']) {
                return 'row-selected';
              } else {
                return '';
              }
            }
          "
          rows-per-page="all"
          ref="rbzTableRef"
          data-qa="events-log-table"
          max-table-height="calc(100% - 130px)"
          @row-clicked="selectPanelEventLog($event as EventLog, props.scrollToElement)"
          @sliced-data-array-display="updatePanelEventLogList($event as EventLog[])"
          :show-top-navbar="true"
          :sticky-top-navbar="true"
          :default-sort-column-index="7"
          default-sort-column-direction="desc"
          :show-new-button="false"
          :show-buttons-column="false"
          @download-data="downloadLog"
        >
          <template #tableResultsOverride="tableResultsProps">
            {{
              $t('eventsLog.eventsCounter', tableResultsProps.numberOfResults, {
                count: tableResultsProps.numberOfResults,
              })
            }}
          </template>
          <template #tableButtonsRightOverride>
            <rbz-dropdown
              :items="eventsLimitOptions"
              :skip-sort="true"
              class="events-limit-selection"
              :header-tooltip="$t('eventsLog.eventsLimitTooltip')"
              data-qa="events-limit-dropdown"
              :ignore-update-in-process="true"
              v-model:value="eventsLimit"
            />
          </template>
          <template #countryFlagCell="cellProps">
            <span class="flag-cell-wrapper">
              <country-flag :country="getCountryCode(cellProps.row.country)" />
            </span>
          </template>
          <template #humanCell="cellProps">
            <span class="human-cell-wrapper">
              <icon-human v-if="cellProps.row.human" />
            </span>
          </template>
          <template #resultCell="cellProps">
            <span
              v-if="cellProps.row.result"
              :class="`result-cell-wrapper ${getResultClass(cellProps.row.result)}`"
            >
              {{
                $t(
                  `eventsLog.resultValue.${resultTranslationMap[getResultClass(cellProps.row.result)]}`
                )
              }}
            </span>
          </template>
          <template #responseCodeCell="cellProps">
            <div class="response-code-cell-wrapper">
              <icon-circle
                :style="{
                  fill: getResponseCodeColor(cellProps.row.status),
                }"
                class="status-circle-icon"
                :bypass-default-color="true"
                circle-style="full"
              />
              <span class="series-title rbz-text-regular">
                {{ cellProps.row.status }}
              </span>
            </div>
          </template>
          <template #tableNoDataOverride v-if="actionRequired">
            {{ $t('eventsLog.actionRequired') }}
          </template>
          <template #customElement="elementProps">
            <events-log-panel
              v-if="panelEventLogIndex !== null"
              :event-log="panelEventLogList[panelEventLogIndex]"
              :anchor-top="elementProps.topNavbar.getBoundingClientRect().bottom"
              :previous-event-log-disabled="panelEventLogIndex === panelEventLogList.length - 1"
              :is-data-optimized="isDataOptimized"
              :next-event-log-disabled="panelEventLogIndex === 0"
              :filtersQuery="filtersQuery"
              @previous-log="changePanelEventLog(1, props.scrollToElement)"
              @next-log="changePanelEventLog(-1, props.scrollToElement)"
              @close-panel="closePanel()"
              @update-event-log="updatePanelEventLogItem($event)"
              @context-menu="
                openContextMenu(
                  $event.event as MouseEvent,
                  panelEventLog,
                  $event.property,
                  $event.value
                )
              "
              @double-click="
                doubleClick(
                  $event.event as PointerEvent,
                  panelEventLog,
                  $event.property,
                  $event.value
                )
              "
            />
          </template>
        </rbz-table>
        <rbz-dropdown-menu
          dropdown-type="contextmenu"
          :context-menu-position="[contextMenuTop, contextMenuLeft]"
          ref="contextMenuRef"
        >
          <template #contextMenuContent>
            <menu-list-item
              :title="$t('eventsLog.contextMenu.copyRequestAsCurl')"
              classes="context-menu-item ignore-fix-focus"
              data-qa="copy-as-curl-button"
              @item-click="contextMenuCopyAsCurl()"
            />
            <layout-divider-line color="light-gray" />
            <menu-list-item
              :title="$t('eventsLog.contextMenu.copyRequestAsJson')"
              classes="context-menu-item ignore-fix-focus"
              data-qa="copy-as-json-button"
              @item-click="contextMenuCopyAsJSON()"
            />
            <layout-divider-line color="light-gray" />
            <menu-list-item
              :title="$t('dashboard.tables.contextMenu.copyValue')"
              classes="context-menu-item ignore-fix-focus"
              data-qa="copy-to-clipboard-button"
              @item-click="contextMenuCopyToClipboard()"
            />
            <layout-divider-line color="light-gray" />
            <menu-list-item
              :title="$t('dashboard.tables.contextMenu.showMatching')"
              classes="context-menu-item ignore-fix-focus"
              data-qa="add-to-filter-positive-button"
              @item-click="contextMenuAddToFilter(false)"
            />
            <layout-divider-line color="light-gray" />
            <menu-list-item
              :title="$t('dashboard.tables.contextMenu.hideMatching')"
              classes="context-menu-item ignore-fix-focus"
              data-qa="add-to-filter-negative-button"
              @item-click="contextMenuAddToFilter(true)"
            />
          </template>
        </rbz-dropdown-menu>
      </div>
    </template>
  </page-template>
</template>

<script lang="ts">
import { defineComponent, nextTick } from 'vue';
import RequestsUtils from '@/helpers/RequestsUtils';
import { Countries, EventLog, GenericObject, ServerGroup } from '@/types';
import _ from 'lodash';
import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import { useUserStore } from '@/store/UserStore';
import RbzFilter from '@/components/RbzFilter.vue';
import RbzFormButton from '@/components/RbzFormButton.vue';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import RbzDropdown from '@/components/RbzDropdown.vue';
import LayoutDividerLine from 'ui-component-library/rbz/components/LayoutDividerLine.vue';
import Utils from 'ui-component-library/helpers/Utils';
import { AxiosError } from 'axios';
import PageTemplate from '@/views/PageTemplate.vue';
import RbzDropdownMenu from '@/components/RbzDropdownMenu.vue';
import { RouteParams } from 'vue-router';
import configuration from '@/configuration/eventsLog.json';
import { useDatasetsStore } from '@/store/DatasetsStore';
import IconHuman from 'ui-component-library/assets/icons/IconHuman.vue';
import IconCircle from 'ui-component-library/assets/icons/IconCircle.vue';
import { RESPONSE_CODE_COLOR_PALETTE } from '@/helpers/Consts';
import CountryFlag from 'ui-component-library/rbz/components/CountryFlag.vue';
import { usePlanetStore } from '@/store/PlanetStore';
import EventsLogPanel from '@/components/EventsLogPanel.vue';
import { RbzTableColumnOptions } from 'ui-component-library/rbz/types';
import { ENV } from '@/constants';
import MenuListItem from 'ui-component-library/rbz/components/MenuListItem.vue';
import { shouldUseMockData, getEventsLogMockData } from '@/helpers/MockDataUtils';

export default defineComponent({
  name: 'EventsLog',
  components: {
    MenuListItem,
    EventsLogPanel,
    CountryFlag,
    RbzFormButton,
    IconCircle,
    IconHuman,
    LayoutDividerLine,
    PageTemplate,
    RbzDropdown,
    RbzDropdownMenu,
    RbzFilter,
    RbzTable,
  },
  data() {
    return {
      actionRequired: true,
      isSearchLoading: false,
      pageLoadingCounter: null as number | null,

      data: null as EventLog[],
      isQueryHistoryLoading: false,
      queryHistory: [] as string[],
      columnOptions: [
        {
          id: 'country',
          title: 'Country',
          hideTitle: true,
          fieldNames: ['country'],
          isSortByOriginalValue: true,
          displaySlotName: 'countryFlagCell',
          tooltipFunction: (item: EventLog) => {
            return item.country || '';
          },
          tooltipFunctionAlwaysDisplayed: true,
          onContextMenu: (event, item: EventLog) => {
            this.openContextMenu(event, item, 'country', item.country);
          },
          isSortable: true,
          classes: 'icon-cell width-40px',
        },
        {
          id: 'ip',
          title: this.$t('eventsLog.ip'),
          fieldNames: ['ip'],
          onContextMenu: (event, item: EventLog) => {
            this.openContextMenu(event, item, 'ip', item.ip);
          },
          isSortable: true,
          isSearchable: true,
          cellContentClasses: 'ellipsis',
          classes: 'width-140px',
        },
        {
          id: 'method',
          title: this.$t('eventsLog.method'),
          fieldNames: ['method'],
          onContextMenu: (event, item: EventLog) => {
            this.openContextMenu(event, item, 'method', item.method);
          },
          isSortable: true,
          isSearchable: true,
          classes: 'width-100px',
        },
        {
          id: 'human',
          title: 'Human',
          hideTitle: true,
          fieldNames: ['human'],
          isSortByOriginalValue: true,
          displaySlotName: 'humanCell',
          tooltipFunction: (item: EventLog) => {
            return item.human ? 'Human' : '';
          },
          onContextMenu: (event, item: EventLog) => {
            this.openContextMenu(event, item, 'human', item.human);
          },
          isSortable: true,
          classes: 'icon-cell width-24px',
        },
        {
          id: 'url',
          title: this.$t('eventsLog.url'),
          fieldNames: ['url'],
          onContextMenu: (event, item: EventLog) => {
            this.openContextMenu(event, item, 'url', item.url);
          },
          isSortable: true,
          isSearchable: true,
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'result',
          title: this.$t('eventsLog.result'),
          fieldNames: ['result'],
          displaySlotName: 'resultCell',
          onContextMenu: (event, item: EventLog) => {
            this.openContextMenu(event, item, 'result', item.result);
          },
          isSortable: true,
          isSearchable: true,
          classes: 'width-220px',
        },
        {
          id: 'status',
          title: this.$t('eventsLog.responseCode'),
          fieldNames: ['status'],
          isSortByOriginalValue: true,
          displaySlotName: 'responseCodeCell',
          tooltipFunction: (item: EventLog) => {
            return item.status;
          },
          onContextMenu: (event, item: EventLog) => {
            this.openContextMenu(event, item, 'status', item.status);
          },
          isSortable: true,
          isSearchable: true,
          isNumber: true,
          classes: 'width-140px',
        },
        {
          id: 'timestamp',
          title: this.$t('eventsLog.dateAndTime'),
          fieldNames: ['timestamp'],
          isSortByOriginalValue: true,
          displayFunction: (item: EventLog) => {
            const splitTimestamp = item.timestamp.split('.');
            splitTimestamp[1] = splitTimestamp[1].slice(0, 2);
            return splitTimestamp.join('.').replace('T', ' ');
          },
          onContextMenu: (event, item: EventLog) => {
            this.openContextMenu(event, item, 'timestamp', item.timestamp);
          },
          isSortable: true,
          isSearchable: true,
          classes: 'width-220px',
        },
      ] as RbzTableColumnOptions[],
      eventsLimitOptions: [
        ['200', this.$t('eventsLog.eventsLimit', { amount: '200' })],
        ['500', this.$t('eventsLog.eventsLimit', { amount: '500' })],
        ['1000', this.$t('eventsLog.eventsLimit', { amount: '1000' })],
        ['1500', this.$t('eventsLog.eventsLimit', { amount: '1500' })],
        ['2000', this.$t('eventsLog.eventsLimit', { amount: '2000' })],
        ['2500', this.$t('eventsLog.eventsLimit', { amount: '2500' })],
      ] as [string | number, string][],
      eventsLimit: '200',
      countries: {} as Countries,
      resultTranslationMap: {
        passed: 'passed',
        challenge: 'challenge',
        'blocked-by-origin': 'blockedByOrigin',
        'blocked-by-waap': 'blockedByWaap',
      } as Record<string, string>,
      filtersQuery: null as string,

      // Panel
      panelEventLogList: [] as EventLog[],
      panelEventLogIndex: null as number,

      // Context Menu
      contextMenuEventLog: null as EventLog,
      contextMenuProperty: null as string,
      contextMenuValue: null as string | number,
      contextMenuTop: '0px',
      contextMenuLeft: '0px',

      // ServerGroup
      selectedServerGroup: null as string | null,
      serverGroups: null as ServerGroup[] | null,
      isPageRefresh: false,
    };
  },
  computed: {
    selectedBranch(): string {
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, useDatasetsStore, usePlanetStore, useUserStore),

    isLoading(): boolean {
      return this.pageLoadingCounter === null || this.pageLoadingCounter > 0;
    },

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    dashboardQuery(): RouteParams {
      const query = this.$route.query?.filters?.toString();
      return { filters: query };
    },

    defaultTime(): number {
      return configuration.defaultTime;
    },

    panelEventLog(): EventLog {
      return this.panelEventLogList[this.panelEventLogIndex];
    },

    serverGroupItems(): [string | number, string][] {
      if (!this.serverGroups) {
        return [];
      }
      return this.serverGroups.map(
        (serverGroup) => [serverGroup.name, serverGroup.name] as [string | number, string]
      );
    },

    isDataOptimized(): boolean {
      return ENV.VITE_EVENTS_LOG_OPTIMIZATION_ENABLED === 'true';
    },
  },
  watch: {
    $route: {
      handler: async function (val) {
        if (val.name.includes('Events Log') && !val.query?.filters) {
          this.data = null;
          this.actionRequired = true;
          this.serverGroups = null;
          this.selectedServerGroup = null;
          nextTick(() => {
            if (
              this.$refs.rbzFilter &&
              typeof this.$refs.rbzFilter.setDataFromRoute === 'function'
            ) {
              this.$refs.rbzFilter.setDataFromRoute();
            }
          });
        }
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    clearSelectedServerGroups() {
      this.selectedServerGroup = null;
    },

    onBlurFilterInput(inputValue: string) {
      this.updateServerGroupDropdown(inputValue);
    },

    updateServerGroupDropdown(inputValue: string) {
      const filters = inputValue.split(/, /);
      const serverGroupFilter = filters.findLast((filter) =>
        filter.trim().startsWith('server_group=')
      );
      if (serverGroupFilter) {
        const serverGroupValue = serverGroupFilter
          .trim()
          .split('=')[1]
          ?.trim()
          .replace(/^"|"$/g, '');
        const selectedServerGroup = this.serverGroups.find(
          (group) => group.name === serverGroupValue.trim()
        );
        this.selectedServerGroup = selectedServerGroup ? selectedServerGroup.name : null;
      } else {
        this.selectedServerGroup = null;
      }
    },

    async loadData(query: string, saveHistory: boolean = true) {
      this.actionRequired = false;
      this.isSearchLoading = true;
      this.closePanel();
      await this.branchesStore.getBranches();
      query += query ? ', ' : '';
      query += `branch=${this.selectedBranch}`;
      this.filtersQuery = query;
      let url = `data/logs?save_history=${saveHistory}&limit=${this.eventsLimit}&filters=${encodeURIComponent(query)}`;
      if (this.isDataOptimized) {
        url = url + '&excluded_fields=arguments';
      }
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: url,
        config: {
          headers: {
            provider: ENV.VITE_LOGS_DB_PROVIDER,
            syntax: 'string_query',
          },
        },
        onFail: (error: AxiosError<{ detail?: string }>) => {
          const data = error.response?.data;
          if (data?.detail) {
            Utils.toast({
              message: data.detail,
              type: 'is-danger',
            });
          }
        },
      });
      this.data = response?.data?.data?.results || [];
      this.data.forEach((dataItem) => {
        dataItem.id = dataItem['request_id'];
      });
      this.loadQueryHistory();
      this.isSearchLoading = false;
    },

    async loadQueryHistory() {
      this.isQueryHistoryLoading = true;
      const currentFilters = this.$route.query?.filters;
      const queryHistoryResponse = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `data/query_history?query_type=events_log`,
        config: {
          headers: {
            accept: 'application/json',
            provider: ENV.VITE_STATE_DB_PROVIDER,
          },
        },
      });
      if (!this.$route.query?.filters && currentFilters) {
        this.restoreFilters(currentFilters);
      }
      this.queryHistory = queryHistoryResponse?.data?.data?.results || [];
      this.isQueryHistoryLoading = false;
    },

    async restoreFilters(currentFilters: string | string[]) {
      const query = { ...this.$route.query, filters: currentFilters };
      await this.$router.replace({
        query,
        params: this.$route.params,
      });
      if (this.$refs.rbzFilter) {
        await this.$nextTick();
        this.$refs.rbzFilter.setDataFromRoute();
      }
    },

    async loadServerGroups() {
      const url = `conf/${this.selectedBranch}/server-groups`;
      const currentFilters = this.$route.query?.filters;

      const serverGroupsResponse = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url,
      });
      if (!this.$route.query?.filters && currentFilters) {
        this.restoreFilters(currentFilters);
      }
      this.serverGroups = serverGroupsResponse?.data?.items || [];
      if (this.$route.query?.filters) {
        let filters = this.$route.query?.filters.toString();
        setTimeout(() => {
          this.updateServerGroupDropdown(filters);
        }, 400);
      }
    },

    loadCountries() {
      this.datasetsStore.getCountries()?.then((data: Countries) => {
        this.countries = data;
      });
    },

    updatePanelEventLogList(eventLogList: EventLog[]) {
      const isListsSameLength = this.panelEventLogList.length === eventLogList.length;
      const isListsDifferent = _.some(this.panelEventLogList, (currentEventLog, index) => {
        return !_.isEqual(currentEventLog, eventLogList[index]);
      });
      if (!isListsSameLength || isListsDifferent) {
        this.panelEventLogList = eventLogList;
        this.closePanel();
      }
    },

    updatePanelEventLogItem(eventLog: EventLog) {
      const index = _.findIndex(this.panelEventLogList, (item: EventLog) => {
        return eventLog.request_id === item.request_id;
      });
      this.panelEventLogList[index] = eventLog;
    },

    selectPanelEventLog(
      eventLog: EventLog,
      scrollToElementFunction: (element: HTMLElement, offsetTop?: number) => void
    ) {
      this.panelEventLogIndex = _.findIndex(this.panelEventLogList, (dataEventLog: EventLog) => {
        return dataEventLog['request_id'] === eventLog['request_id'];
      });
      nextTick(() => {
        const selectedRow = this.$refs.rbzTableRef.$el.querySelector('.row-selected');
        scrollToElementFunction(selectedRow as HTMLElement, 101);
      });
    },

    changePanelEventLog(
      value: number,
      scrollToElementFunction: (element: HTMLElement, offsetTop?: number) => void
    ) {
      const newIndex = this.panelEventLogIndex + value;
      if (newIndex >= 0 && newIndex <= this.panelEventLogList.length - 1) {
        this.panelEventLogIndex = newIndex;
      }
      nextTick(() => {
        const selectedRow = this.$refs.rbzTableRef.$el.querySelector('.row-selected');
        scrollToElementFunction(selectedRow as HTMLElement, 101);
      });
    },

    closePanel() {
      this.panelEventLogIndex = null;
    },

    getCountryCode(countryName: string) {
      if (!countryName) {
        return '';
      }
      const countryCode = Object.keys(this.countries).find((key: keyof Countries) => {
        return this.countries[key].toLowerCase() === countryName.toLowerCase();
      });
      return _.escape(countryCode?.toLowerCase() || '');
    },

    getResponseCodeColor(responseCode: number): string {
      const colorPalette = RESPONSE_CODE_COLOR_PALETTE as GenericObject;
      const colorClass = `${responseCode?.toString().slice(0, -2)}xx`;
      return colorPalette[colorClass]?.[0] || '#000';
    },

    getResultClass(result: string) {
      return result.toLowerCase().replaceAll(' ', '-') || '';
    },

    downloadLog() {
      Utils.downloadFile('logs', 'json', this.data);
    },

    doubleClick(event: PointerEvent, eventLog: EventLog, property: string, value: string | number) {
      this.$refs.contextMenuRef.openMenu();
      this.contextMenuEventLog = eventLog;
      this.contextMenuProperty = property;
      this.contextMenuValue = value;
      this.contextMenuAddToFilter(false);
      event.preventDefault();
    },

    setContextMenu(top: number, left: number) {
      const largestHeight =
        window.innerHeight - this.$refs.contextMenuRef.contextMenu.offsetHeight - 25;
      const largestWidth =
        window.innerWidth - this.$refs.contextMenuRef.contextMenu.offsetWidth - 25;
      if (top > largestHeight) {
        top = largestHeight;
      }
      if (left > largestWidth) {
        left = largestWidth;
      }
      this.contextMenuTop = top + 'px';
      this.contextMenuLeft = left + 'px';
    },

    closeContextMenu() {
      this.$refs.contextMenuRef.closeMenu();
      this.contextMenuEventLog = null;
      this.contextMenuProperty = null;
      this.contextMenuValue = null;
      document
        .getElementsByClassName('main-content')[0]
        .removeEventListener('scroll', this.closeContextMenu);
    },

    openContextMenu(
      event: MouseEvent,
      eventLog: EventLog,
      property: string,
      value: string | number
    ) {
      this.$refs.contextMenuRef.openMenu();
      this.contextMenuEventLog = eventLog;
      this.contextMenuProperty = property;
      this.contextMenuValue = value;
      nextTick(() => {
        this.setContextMenu(event.y, event.x);
        setTimeout(() => {
          document
            .getElementsByClassName('main-content')[0]
            .addEventListener('scroll', this.closeContextMenu);
        }, 100);
      });
      event.preventDefault();
    },

    contextMenuCopyAsCurl() {
      let value = `curl`;
      value += ` -X '${this.contextMenuEventLog.method}'`;
      let protocolPrefix = '';
      if (this.contextMenuEventLog.headers?.['x-forwarded-proto']) {
        protocolPrefix = `${this.contextMenuEventLog.headers['x-forwarded-proto']}://`;
      }
      value += ` '${protocolPrefix}${this.contextMenuEventLog.url}'`;
      if (this.contextMenuEventLog.method?.toUpperCase() !== 'GET') {
        const urlParams = new URLSearchParams(this.contextMenuEventLog.query);
        _.forEach(this.contextMenuEventLog.arguments, (argumentValue, argumentKey) => {
          const queryValue = urlParams.get(argumentKey);
          if (queryValue) {
            argumentValue = argumentValue.replace(queryValue, '');
          }
          argumentValue = argumentValue.trim();
          if (argumentValue) {
            const encodedArgumentValue = encodeURIComponent(argumentValue);
            value += ` -d '${argumentKey}=${encodedArgumentValue}'`;
          }
        });
      }
      _.forEach(this.contextMenuEventLog.headers, (headerValue, headerKey) => {
        value += ` -H '${headerKey}: ${headerValue}'`;
      });
      _.forEach(this.contextMenuEventLog.cookies, (cookieValue, cookieKey) => {
        value += ` -H 'Cookie: ${cookieKey}=${cookieValue}'`;
      });
      value += ` -H 'curl-generated-by-WAAP: ${this.planetStore.version}'`;
      navigator.clipboard.writeText(value);
      this.closeContextMenu();
    },

    contextMenuCopyAsJSON() {
      navigator.clipboard.writeText(JSON.stringify(this.contextMenuEventLog));
      this.closeContextMenu();
    },

    contextMenuCopyToClipboard() {
      navigator.clipboard.writeText(this.contextMenuValue?.toString());
      this.closeContextMenu();
    },

    addServerGroupToFilter(host: string | string[]) {
      const resolvedHost = Array.isArray(host) ? host[0] : host;
      this.selectedServerGroup = resolvedHost;
      const filterString = 'server_group="' + resolvedHost + '"';
      this.$refs.rbzFilter.addServerGroupFilter(filterString);
    },

    contextMenuAddToFilter(isNegative?: boolean) {
      const operator: string = this.contextMenuProperty;
      let operand: string | number | boolean = this.contextMenuValue;
      if (typeof operand === 'string') {
        operand = `"${operand.replace(/"/g, '\\"')}"`;
      }
      let filterString;
      if (typeof operand === 'boolean') {
        filterString = `${operator}=${isNegative ? !operand : operand}`;
      } else {
        filterString = `${operator}${isNegative ? '!' : ''}=${operand}`;
      }
      this.$refs.rbzFilter.addFilter(filterString);
      this.closeContextMenu();
    },
  },
  async mounted() {
    this.setPageLoadingStatus(true);
    this.loadCountries();
    const loadQueryPromise = this.loadQueryHistory();
    const serverGroupPromise = this.loadServerGroups();
    await Promise.all([await loadQueryPromise, await serverGroupPromise]);
    this.setPageLoadingStatus(false);

    // Only load mock data if there are filters in the route (similar to real data loading)
    if (shouldUseMockData() && this.$route.query?.filters) {
      const mock = getEventsLogMockData();
      this.data = mock.data;
      this.serverGroups = mock.serverGroups;
      this.filtersQuery = mock.filtersQuery;
      this.actionRequired = false;
    }
  },
});
</script>
<style scoped lang="scss">
@use 'ui-component-library/assets/styles/colors' as colors;
@use 'sass:map';

:deep(.rbz-page-header .server-group) {
  .dropdown-header {
    max-width: 190px;
    background-color: map.get(colors.$colors, 'mountain-meadow-light');
    color: map.get(colors.$colors, 'white');
  }

  .dropdown-header.open {
    border-radius: 4px;
    border: 0;
  }

  .dropdown-header > .tags-wrapper > .custom-tag {
    background-color: map.get(colors.$colors, 'mountain-meadow-light');
    color: map.get(colors.$colors, 'white');
  }

  .tag-content {
    font-size: 14px;
  }

  .custom-tag > .remove-button {
    display: none;
  }

  .dropdown-header > .scrollbox-shadowed {
    background: map.get(colors.$colors, 'mountain-meadow-light');
  }

  .dropdown-placeholder {
    color: map.get(colors.$colors, 'white');
    max-width: 135px;
  }

  .expand-icon-wrapper > .chevron {
    fill: map.get(colors.$colors, 'white');
  }

  .dropdown-list {
    top: 40px;
    border-radius: 4px;
    border-top-width: 1px;
    width: 416px;
    right: 0;
  }
}

.action-required-panel {
  padding: 10px 15px;
  text-align: center;
}

.events-limit-selection {
  width: 181px;
}

.events-log-table-wrapper {
  background-color: inherit;
  position: relative;
}

:deep(.rbz-table-wrapper) {
  $row-height: 40px;

  .top-navbar {
    .table-buttons {
      margin: 6px 16px 5px;
    }
  }

  &.rbz-table-panel-open {
    .rbz-table {
      width: calc(100% - 626px);
    }
  }

  th {
    border-bottom: 1px solid map.get(colors.$colors, 'silver');
    height: $row-height;
  }

  td {
    border-bottom: 0.5px solid map.get(colors.$colors, 'silver');
    height: $row-height;
    padding: 11px 12px;
  }

  .icon-cell {
    padding: 0;
  }

  .flag-cell-wrapper {
    align-items: center;
    display: flex;
    justify-content: center;
    overflow: hidden;

    .flag {
      border-radius: 1.5px;
      margin: -0.9em -1.2em;
      transform: scale(0.33);
    }

    .flag-placeholder {
      border-radius: 6px;
      margin: -0.9em -1.2em;
      transform: scale(0.33);
    }
  }

  .result-cell-wrapper {
    align-items: center;
    border-radius: 4px;
    display: inline-flex;
    flex-direction: column;
    flex-shrink: 0;
    gap: 4px;
    height: 24px;
    justify-content: center;
    padding: 4px 8px;

    &.passed {
      background-color: map.get(colors.$colors, 'peppermint');
    }

    &.challenge {
      background-color: map.get(colors.$colors, 'buttermilk');
    }

    &.blocked-by-origin {
      background-color: map.get(colors.$colors, 'melon');
    }

    &.blocked-by-waap {
      background-color: map.get(colors.$colors, 'melon');
      border: 1px solid map.get(colors.$colors, 'tabasco');
    }
  }

  .response-code-cell-wrapper {
    align-items: center;
    color: map.get(colors.$colors, 'bright-gray');
    display: flex;
    gap: 4px;
  }

  .row-selected {
    background: map.get(colors.$colors, 'hawkes-blue');
  }
}
</style>
